import { supabase, getCurrentOrganizationId } from './supabaseClient';
import type { Tables, TablesInsert, TablesUpdate, Json } from '../types/supabase';
import type {
  Client,
  Case,
  PaymentPlan,
  PaymentInstallment,
  PaymentLink,
  BitacoraEntry,
  Expense,
  ExpenseType,
  MonthlyExpenseSummary,
  ExpenseCategory,
  ConsentStatus,
  ConsentUpdate,
  ConsentHistoryEntry,
  DeletionRequest,
  UserDataExport,
  UserSession,
  LoginAttempt,
  MFAConfig,
  MFASetupResponse,
  PasswordValidationResult,
  SecuritySettings,
  AuditLog,
  AuditLogFilters,
  SecurityDashboardData,
  SecurityEvent,
  SecurityEventFilters,
  RateLimitInfo,
  RateLimitCheckResult,
  EncryptionKey,
  KeyRotationStatus,
  KeyRotationResult,
  Plazo,
  PlazoFormValues,
  AppNotification,
  FeatureWaitlistEntry,
} from '../types';

// Type aliases for Supabase tables
type DbPaymentPlan = Tables<'payment_plans'>;
type DbExpense = Tables<'expenses'>;

// Edge Function URLs for secure operations (encryption handled server-side)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const CLIENT_SERVICE_URL = `${SUPABASE_URL}/functions/v1/client-service`;
const CASE_SERVICE_URL = `${SUPABASE_URL}/functions/v1/case-service`;
const BITACORA_SERVICE_URL = `${SUPABASE_URL}/functions/v1/bitacora-service`;
const AUTH_SERVICE_URL = `${SUPABASE_URL}/functions/v1/auth-service`;
const USER_DATA_SERVICE_URL = `${SUPABASE_URL}/functions/v1/user-data-service`;
const AUDIT_SERVICE_URL = `${SUPABASE_URL}/functions/v1/audit-service`;
const DATA_LIFECYCLE_SERVICE_URL = `${SUPABASE_URL}/functions/v1/data-lifecycle-service`;
const SECURITY_MONITOR_URL = `${SUPABASE_URL}/functions/v1/security-monitor`;
const KEY_MANAGEMENT_URL = `${SUPABASE_URL}/functions/v1/key-management-service`;
const EMAIL_SERVICE_URL = `${SUPABASE_URL}/functions/v1/email-service`;
const FEATURE_WAITLIST_SERVICE_URL = `${SUPABASE_URL}/functions/v1/feature-waitlist-service`;

// Generic helper to call Edge Functions with authentication
async function callEdgeFunction<T>(
  baseUrl: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('User not authenticated');
  }

  let url = path ? `${baseUrl}/${path}` : baseUrl;
  if (queryParams) {
    const params = new URLSearchParams(queryParams);
    url += `?${params.toString()}`;
  }
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
  };

  const options: RequestInit = { method, headers };
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  let response = await fetch(url, options);

  // On 401, refresh the session and retry once
  if (response.status === 401) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    if (refreshData.session?.access_token) {
      options.headers = {
        ...options.headers as Record<string, string>,
        'Authorization': `Bearer ${refreshData.session.access_token}`,
      };
      response = await fetch(url, options);
    }
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
  }

  return data;
}

// Blob variant of callEdgeFunction — for endpoints that return file downloads
async function callEdgeFunctionBlob(
  baseUrl: string,
  path: string = '',
  queryParams?: Record<string, string>
): Promise<Blob> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('User not authenticated');
  }

  let url = path ? `${baseUrl}/${path}` : baseUrl;
  if (queryParams) {
    url += `?${new URLSearchParams(queryParams).toString()}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
    },
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  return response.blob();
}

// Helper to call Client Edge Function with authentication
async function callClientService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<T> {
  return callEdgeFunction<T>(CLIENT_SERVICE_URL, method, path, body, queryParams);
}

// Helper to call Case Edge Function with authentication
async function callCaseService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<T> {
  return callEdgeFunction<T>(CASE_SERVICE_URL, method, path, body, queryParams);
}

// Helper to call Bitacora Edge Function with authentication
async function callBitacoraService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(BITACORA_SERVICE_URL, method, path, body);
}

// Helper to call Auth Service Edge Function with authentication
export async function callAuthService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(AUTH_SERVICE_URL, method, path, body);
}

// Helper to call Audit Service Edge Function with authentication
async function callAuditService<T>(
  method: 'GET' | 'POST',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(AUDIT_SERVICE_URL, method, path, body);
}

// Helper to call Data Lifecycle Service Edge Function with authentication
async function callDataLifecycleService<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(DATA_LIFECYCLE_SERVICE_URL, method, path, body);
}

// Helper to call Security Monitor Edge Function with authentication
async function callSecurityMonitor<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(SECURITY_MONITOR_URL, method, path, body);
}

// Helper to call Key Management Edge Function with authentication
async function callKeyManagement<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(KEY_MANAGEMENT_URL, method, path, body);
}

// Helper to call User Data Service Edge Function with authentication
async function callUserDataService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(USER_DATA_SERVICE_URL, method, path, body);
}

// Helper to call Plazo Service Edge Function with authentication
const PLAZO_SERVICE_URL = `${SUPABASE_URL}/functions/v1/plazo-service`;

async function callPlazoService<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string = '',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<T> {
  return callEdgeFunction<T>(PLAZO_SERVICE_URL, method, path, body, queryParams);
}

// Helper to call Email Service Edge Function with authentication
async function callEmailService<T>(
  method: 'POST',
  path: string = '',
  body?: Record<string, unknown>
): Promise<T> {
  return callEdgeFunction<T>(EMAIL_SERVICE_URL, method, path, body);
}

// Helper to call Feature Waitlist Service Edge Function with authentication
async function callFeatureWaitlistService<T>(
  method: 'GET' | 'POST',
  path: string = '',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
): Promise<T> {
  return callEdgeFunction<T>(FEATURE_WAITLIST_SERVICE_URL, method, path, body, queryParams);
}

// Map Edge Function response to frontend Client type
function mapClientResponse(client: any): Client {
  return {
    id: client.id,
    name: client.name || '',
    rut: client.rut || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    clientType: client.clientType || 'natural',
    active: client.active ?? true,
    createdAt: client.createdAt || new Date().toISOString(),
    updatedAt: client.updatedAt || new Date().toISOString(),
  };
}

// Map Edge Function response to frontend Case type
function mapCaseResponse(caseData: any, client?: Client): Case {
  return {
    id: caseData.id,
    tipoCausa1: caseData.tipoCausa1 || '',
    tipoCausa2: caseData.tipoCausa2 || '',
    materia: caseData.materia || '',
    tribunal: caseData.tribunal || '',
    numeroCausa: caseData.numeroCausa || '',
    nombreCausa: caseData.nombreCausa || '',
    fechaInicio: caseData.fechaInicio || '',
    estado: caseData.estado || null,
    pasoActual: caseData.pasoActual || '',
    bitacoraActual: caseData.bitacoraActual || '',
    clientId: caseData.clientId || undefined,
    client: client,
    archived: caseData.archived ?? false,
    caseMetadata: caseData.caseMetadata || undefined,
    createdAt: caseData.createdAt || new Date().toISOString(),
    updatedAt: caseData.updatedAt || new Date().toISOString(),
  };
}

// Map Edge Function response to frontend BitacoraEntry type
function mapBitacoraResponse(entry: any): BitacoraEntry {
  return {
    id: entry.id,
    caseId: entry.caseId,
    content: entry.content || '',
    createdAt: entry.createdAt || new Date().toISOString(),
    updatedAt: entry.updatedAt || new Date().toISOString(),
    createdBy: entry.createdBy || undefined,
  };
}

// Cache for decrypted client data to avoid repeated Edge Function calls
const clientCache = new Map<string, { data: Client; timestamp: number }>();
const CACHE_TTL_MS = 900000; // 15 minute cache

// Helper to fetch and cache decrypted client data via Edge Function
async function fetchDecryptedClient(clientId: string): Promise<Client | undefined> {
  if (!clientId) return undefined;

  // Check cache first
  const cached = clientCache.get(clientId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const client = await callClientService<any>('GET', clientId);
    const mappedClient = mapClientResponse(client);
    clientCache.set(clientId, { data: mappedClient, timestamp: Date.now() });
    return mappedClient;
  } catch (error) {
    console.error('Failed to fetch client:', clientId, error);
    return undefined;
  }
}

// Clear client cache (useful after updates)
function clearClientCache(clientId?: string) {
  if (clientId) {
    clientCache.delete(clientId);
  } else {
    clientCache.clear();
  }
}

// Map database payment plan to frontend PaymentPlan type
function mapDbPaymentPlanToPaymentPlan(dbPlan: DbPaymentPlan, caseData?: Case): PaymentPlan {
  const installments = (dbPlan.installments as unknown as PaymentInstallment[]) || [];
  
  return {
    id: dbPlan.id,
    caseId: dbPlan.case_id,
    case: caseData,
    totalAmount: dbPlan.total_amount,
    numberOfInstallments: dbPlan.number_of_installments || installments.length,
    paymentSchedule: installments,
    isRecurring: dbPlan.is_recurring ?? false,
    recurringFrequency: dbPlan.recurring_frequency || undefined,
    recurringAmount: dbPlan.recurring_amount || undefined,
    recurringDayOfMonth: dbPlan.recurring_day_of_month || undefined,
    createdAt: dbPlan.created_at || new Date().toISOString(),
    updatedAt: dbPlan.updated_at || new Date().toISOString(),
  };
}

// Map database expense to frontend Expense type
function mapDbExpenseToExpense(dbExpense: DbExpense): Expense {
  return {
    id: dbExpense.id,
    type: dbExpense.type || 'single',
    name: dbExpense.name,
    amount: dbExpense.amount,
    category: dbExpense.category || 'other',
    vendor: dbExpense.vendor || undefined,
    paymentMethod: dbExpense.payment_method || undefined,
    frequency: dbExpense.frequency || undefined,
    dueDate: dbExpense.due_date || undefined,
    startDate: dbExpense.start_date || new Date().toISOString(),
    endDate: dbExpense.end_date || undefined,
    isAmortized: dbExpense.is_amortized ?? false,
    amortizationMonths: dbExpense.amortization_months || undefined,
    expenseDate: dbExpense.expense_date || undefined,
    receiptUrl: dbExpense.receipt_url || undefined,
    notes: dbExpense.notes || undefined,
    createdAt: dbExpense.created_at || new Date().toISOString(),
    updatedAt: dbExpense.updated_at || new Date().toISOString(),
  };
}

// API service layer using Supabase
export const apiService = {
  // Cache management (for clearing PII on logout)
  cache: {
    clearAll: () => {
      clientCache.clear();
    },
  },

  // Client endpoints - using Edge Function for secure PII encryption
  clients: {
    getAll: async (): Promise<Client[]> => {
      const clients = await callClientService<any[]>('GET');
      return clients.map(mapClientResponse);
    },

    getAllPaginated: async (params: { limit: number; offset: number }): Promise<{ data: Client[]; total: number; limit: number; offset: number }> => {
      const result = await callClientService<{ data: any[]; total: number; limit: number; offset: number }>(
        'GET', '', undefined,
        { limit: String(params.limit), offset: String(params.offset) }
      );
      return {
        data: result.data.map(mapClientResponse),
        total: result.total,
        limit: result.limit,
        offset: result.offset,
      };
    },

    getById: async (id: string): Promise<Client> => {
      const client = await callClientService<any>('GET', id);
      return mapClientResponse(client);
    },

    create: async (clientData: {
      name: string;
      rut: string;
      email: string;
      phone: string;
      address?: string;
      clientType?: 'natural' | 'juridica';
    }): Promise<Client> => {
      const client = await callClientService<any>('POST', '', clientData);
      return mapClientResponse(client);
    },

    update: async (id: string, clientData: Partial<{
      name: string;
      rut: string;
      email: string;
      phone: string;
      address?: string;
      clientType?: 'natural' | 'juridica';
      active?: boolean;
    }>): Promise<Client> => {
      const client = await callClientService<any>('PUT', id, clientData);
      return mapClientResponse(client);
    },

    delete: async (id: string): Promise<void> => {
      await callClientService<any>('DELETE', id);
    },
  },

  // Case endpoints - using Edge Function for secure PII encryption
  cases: {
    getAll: async (): Promise<Case[]> => {
      // Use ?include=client to batch-fetch client data in a single request
      const cases = await callCaseService<any[]>('GET', '', undefined, { include: 'client' });

      return cases.map((caseData) => {
        // Build Client object from inline data if available
        const client: Client | undefined = (caseData.clientId && caseData.clientName)
          ? {
              id: caseData.clientId,
              name: caseData.clientName,
              rut: caseData.clientRut || '',
              email: caseData.clientEmail || '',
              phone: caseData.clientPhone || '',
              address: caseData.clientAddress || '',
              clientType: caseData.clientType || 'natural',
              active: caseData.clientActive ?? true,
              createdAt: '',
              updatedAt: '',
            }
          : undefined;
        return mapCaseResponse(caseData, client);
      });
    },

    getAllPaginated: async (params: { limit: number; offset: number }): Promise<{ data: Case[]; total: number; limit: number; offset: number }> => {
      const result = await callCaseService<{ data: any[]; total: number; limit: number; offset: number }>(
        'GET', '', undefined,
        { limit: String(params.limit), offset: String(params.offset), include: 'client' }
      );

      const cases = result.data.map((caseData) => {
        const client: Client | undefined = (caseData.clientId && caseData.clientName)
          ? {
              id: caseData.clientId,
              name: caseData.clientName,
              rut: caseData.clientRut || '',
              email: caseData.clientEmail || '',
              phone: caseData.clientPhone || '',
              address: caseData.clientAddress || '',
              clientType: caseData.clientType || 'natural',
              active: caseData.clientActive ?? true,
              createdAt: '',
              updatedAt: '',
            }
          : undefined;
        return mapCaseResponse(caseData, client);
      });

      return { data: cases, total: result.total, limit: result.limit, offset: result.offset };
    },

    getById: async (id: string): Promise<Case> => {
      const caseData = await callCaseService<any>('GET', id);
      const client = caseData.clientId
        ? await fetchDecryptedClient(caseData.clientId)
        : undefined;
      return mapCaseResponse(caseData, client);
    },

    create: async (caseData: {
      tipoCausa1: string;
      tipoCausa2: string;
      materia: string;
      tribunal: string;
      numeroCausa: string;
      nombreCausa: string;
      fechaInicio: string;
      estado: string | null;
      pasoActual: string;
      bitacoraActual: string;
      clientId?: string;
      caseMetadata?: Record<string, unknown>;
    }): Promise<Case> => {
      const result = await callCaseService<any>('POST', '', caseData);
      const client = result.clientId 
        ? await fetchDecryptedClient(result.clientId) 
        : undefined;
      return mapCaseResponse(result, client);
    },

    update: async (id: string, caseData: Partial<{
      tipoCausa1: string;
      tipoCausa2: string;
      materia: string;
      tribunal: string;
      numeroCausa: string;
      nombreCausa: string;
      fechaInicio: string;
      estado: string | null;
      pasoActual: string;
      bitacoraActual: string;
      clientId?: string;
      caseMetadata?: Record<string, unknown>;
    }>): Promise<Case> => {
      const result = await callCaseService<any>('PUT', id, caseData);
      const client = result.clientId 
        ? await fetchDecryptedClient(result.clientId) 
        : undefined;
      return mapCaseResponse(result, client);
    },

    delete: async (id: string): Promise<void> => {
      await callCaseService<any>('DELETE', id);
    },

    archive: async (id: string): Promise<Case> => {
      const result = await callCaseService<any>('POST', `${id}/archive`);
      const client = result.clientId 
        ? await fetchDecryptedClient(result.clientId) 
        : undefined;
      return mapCaseResponse(result, client);
    },

    unarchive: async (id: string): Promise<Case> => {
      const result = await callCaseService<any>('POST', `${id}/unarchive`);
      const client = result.clientId 
        ? await fetchDecryptedClient(result.clientId) 
        : undefined;
      return mapCaseResponse(result, client);
    },

    getArchived: async (): Promise<Case[]> => {
      // Use ?include=client to batch-fetch client data in a single request
      const cases = await callCaseService<any[]>('GET', 'archived', undefined, { include: 'client' });

      return cases.map((caseData) => {
        const client: Client | undefined = (caseData.clientId && caseData.clientName)
          ? {
              id: caseData.clientId,
              name: caseData.clientName,
              rut: caseData.clientRut || '',
              email: caseData.clientEmail || '',
              phone: caseData.clientPhone || '',
              address: caseData.clientAddress || '',
              clientType: caseData.clientType || 'natural',
              active: caseData.clientActive ?? true,
              createdAt: '',
              updatedAt: '',
            }
          : undefined;
        return mapCaseResponse(caseData, client);
      });
    },

    updateOrder: async (caseIds: string[]): Promise<void> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Delete existing order entries for this user
      await supabase
        .from('user_case_orders')
        .delete()
        .eq('user_id', user.id);

      // Insert new order entries
      const orderEntries = caseIds.map((caseId, index) => ({
        user_id: user.id,
        case_id: caseId,
        order: index,
      }));

      const { error } = await supabase
        .from('user_case_orders')
        .insert(orderEntries);

      if (error) throw error;
    },
  },

  // Payment Plan endpoints
  paymentPlans: {
    getAll: async (): Promise<PaymentPlan[]> => {
      const { data, error } = await supabase
        .from('payment_plans')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Batch-fetch all cases with client data in a single request
      const uniqueCaseIds = Array.from(new Set((data || []).map(p => p.case_id).filter(Boolean))) as string[];
      const caseMap = new Map<string, Case>();

      if (uniqueCaseIds.length > 0) {
        try {
          // Use the batch endpoint to get all cases with embedded client data
          const allCases = await callCaseService<any[]>('GET', '', undefined, { include: 'client' });
          for (const caseData of allCases) {
            const client: Client | undefined = (caseData.clientId && caseData.clientName)
              ? {
                  id: caseData.clientId,
                  name: caseData.clientName,
                  rut: caseData.clientRut || '',
                  email: caseData.clientEmail || '',
                  phone: caseData.clientPhone || '',
                  address: caseData.clientAddress || '',
                  clientType: caseData.clientType || 'natural',
                  active: caseData.clientActive ?? true,
                  createdAt: '',
                  updatedAt: '',
                }
              : undefined;
            caseMap.set(caseData.id, mapCaseResponse(caseData, client));
          }
        } catch (e) {
          console.error('Failed to batch-fetch cases for payment plans:', e);
        }
      }

      return (data || []).map((dbPlan) => {
        const caseData = dbPlan.case_id ? caseMap.get(dbPlan.case_id) : undefined;
        return mapDbPaymentPlanToPaymentPlan(dbPlan, caseData);
      });
    },

    getById: async (id: string): Promise<PaymentPlan> => {
      const { data, error } = await supabase
        .from('payment_plans')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      let caseData: Case | undefined;
      if (data.case_id) {
        try {
          // Fetch case through Edge Function to get decrypted data
          const caseResult = await callCaseService<any>('GET', data.case_id);
          const client = caseResult.clientId 
            ? await fetchDecryptedClient(caseResult.clientId) 
            : undefined;
          caseData = mapCaseResponse(caseResult, client);
        } catch (e) {
          console.error('Failed to fetch case for payment plan:', e);
        }
      }
      return mapDbPaymentPlanToPaymentPlan(data, caseData);
    },

    create: async (planData: {
      caseId: string;
      totalAmount: number;
      numberOfInstallments: number;
      paymentSchedule?: PaymentInstallment[];
      isRecurring?: boolean;
      recurringFrequency?: 'monthly' | 'quarterly' | 'yearly';
      recurringAmount?: number;
      recurringDayOfMonth?: number;
    }): Promise<PaymentPlan> => {
      const orgId = await getCurrentOrganizationId();
      if (!orgId) throw new Error('No organization found for user');

      const { data: { user } } = await supabase.auth.getUser();

      const insertData: TablesInsert<'payment_plans'> = {
        organization_id: orgId,
        case_id: planData.caseId,
        total_amount: planData.totalAmount,
        number_of_installments: planData.numberOfInstallments,
        installments: (planData.paymentSchedule || []) as unknown as Json,
        is_recurring: planData.isRecurring || false,
        recurring_frequency: planData.recurringFrequency || null,
        recurring_amount: planData.recurringAmount || null,
        recurring_day_of_month: planData.recurringDayOfMonth || null,
        created_by: user?.id,
      };

      const { data, error } = await supabase
        .from('payment_plans')
        .insert(insertData)
        .select('*')
        .single();

      if (error) throw error;

      let caseData: Case | undefined;
      if (data.case_id) {
        try {
          const caseResult = await callCaseService<any>('GET', data.case_id);
          const client = caseResult.clientId 
            ? await fetchDecryptedClient(caseResult.clientId) 
            : undefined;
          caseData = mapCaseResponse(caseResult, client);
        } catch (e) {
          console.error('Failed to fetch case for payment plan:', e);
        }
      }
      return mapDbPaymentPlanToPaymentPlan(data, caseData);
    },

    update: async (id: string, planData: Partial<{
      totalAmount: number;
      numberOfInstallments: number;
      paymentSchedule?: PaymentInstallment[];
      isRecurring?: boolean;
      recurringFrequency?: 'monthly' | 'quarterly' | 'yearly';
      recurringAmount?: number;
      recurringDayOfMonth?: number;
    }>): Promise<PaymentPlan> => {
      const { data: { user } } = await supabase.auth.getUser();

      const updateData: TablesUpdate<'payment_plans'> = {
        updated_by: user?.id,
        updated_at: new Date().toISOString(),
      };

      if (planData.totalAmount !== undefined) updateData.total_amount = planData.totalAmount;
      if (planData.numberOfInstallments !== undefined) updateData.number_of_installments = planData.numberOfInstallments;
      if (planData.paymentSchedule !== undefined) updateData.installments = planData.paymentSchedule as unknown as Json;
      if (planData.isRecurring !== undefined) updateData.is_recurring = planData.isRecurring;
      if (planData.recurringFrequency !== undefined) updateData.recurring_frequency = planData.recurringFrequency;
      if (planData.recurringAmount !== undefined) updateData.recurring_amount = planData.recurringAmount;
      if (planData.recurringDayOfMonth !== undefined) updateData.recurring_day_of_month = planData.recurringDayOfMonth;

      const { data, error } = await supabase
        .from('payment_plans')
        .update(updateData)
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw error;

      let caseData: Case | undefined;
      if (data.case_id) {
        try {
          const caseResult = await callCaseService<any>('GET', data.case_id);
          const client = caseResult.clientId 
            ? await fetchDecryptedClient(caseResult.clientId) 
            : undefined;
          caseData = mapCaseResponse(caseResult, client);
        } catch (e) {
          console.error('Failed to fetch case for payment plan:', e);
        }
      }
      return mapDbPaymentPlanToPaymentPlan(data, caseData);
    },

    getProjectedIncome: async (): Promise<{
      monthly: { month: string; amount: number }[];
      total: number;
    }> => {
      const { data, error } = await supabase
        .from('payment_plans')
        .select('installments, total_amount');

      if (error) throw error;

      // Calculate projected income from unpaid installments
      const monthlyMap = new Map<string, number>();
      let total = 0;

      (data || []).forEach((plan) => {
        const installments = (plan.installments as unknown as PaymentInstallment[]) || [];
        installments
          .filter((inst) => inst.status === 'unpaid')
          .forEach((inst) => {
            const monthKey = inst.dueDate?.substring(0, 7) || 'unknown';
            const current = monthlyMap.get(monthKey) || 0;
            monthlyMap.set(monthKey, current + inst.amount);
            total += inst.amount;
          });
      });

      const monthly = Array.from(monthlyMap.entries())
        .map(([month, amount]) => ({ month, amount }))
        .sort((a, b) => a.month.localeCompare(b.month));

      return { monthly, total };
    },
  },

  // Payment Link endpoints
  paymentLinks: {
    getAll: async (): Promise<PaymentLink[]> => {
      const { data, error } = await supabase
        .from('payment_links')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((link) => ({
        id: link.id,
        caseId: link.case_id,
        amount: link.amount,
        description: link.description,
        link: link.link || '',
        status: (link.status as 'pending' | 'paid' | 'expired') || 'pending',
        createdAt: link.created_at || new Date().toISOString(),
      }));
    },

    create: async (linkData: {
      caseId: string;
      amount: number;
      description: string;
    }): Promise<PaymentLink> => {
      const insertData: TablesInsert<'payment_links'> = {
        case_id: linkData.caseId,
        amount: linkData.amount,
        description: linkData.description,
        status: 'pending',
      };

      const { data, error } = await supabase
        .from('payment_links')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        caseId: data.case_id,
        amount: data.amount,
        description: data.description,
        link: data.link || '',
        status: (data.status as 'pending' | 'paid' | 'expired') || 'pending',
        createdAt: data.created_at || new Date().toISOString(),
      };
    },
  },

  // Bitácora endpoints - using Edge Function for secure PII encryption
  bitacora: {
    getByCaseId: async (caseId: string): Promise<BitacoraEntry[]> => {
      const entries = await callBitacoraService<any[]>('GET', `case/${caseId}`);
      return entries.map(mapBitacoraResponse);
    },

    getLatestByCaseIds: async (caseIds: string[]): Promise<Record<string, BitacoraEntry>> => {
      const result = await callBitacoraService<Record<string, any>>('POST', 'latest-by-cases', { caseIds } as any);
      const mapped: Record<string, BitacoraEntry> = {};
      for (const [caseId, entry] of Object.entries(result)) {
        if (entry) mapped[caseId] = mapBitacoraResponse(entry);
      }
      return mapped;
    },

    create: async (entryData: {
      caseId: string;
      content: string;
      entryType?: string;
      eventDate?: string;
    }): Promise<BitacoraEntry> => {
      const result = await callBitacoraService<any>('POST', '', entryData);
      return mapBitacoraResponse(result);
    },

    update: async (id: string, entryData: Partial<{
      content: string;
      entryType?: string;
      eventDate?: string;
    }>): Promise<BitacoraEntry> => {
      const result = await callBitacoraService<any>('PUT', id, entryData);
      return mapBitacoraResponse(result);
    },

    delete: async (id: string): Promise<void> => {
      await callBitacoraService<any>('DELETE', id);
    },
  },

  // Expense endpoints
  expenses: {
    getAll: async (): Promise<Expense[]> => {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(mapDbExpenseToExpense);
    },

    getByType: async (type: ExpenseType): Promise<Expense[]> => {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .eq('type', type)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(mapDbExpenseToExpense);
    },

    getById: async (id: string): Promise<Expense> => {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return mapDbExpenseToExpense(data);
    },

    create: async (expenseData: {
      type: ExpenseType;
      name: string;
      amount: number;
      category: ExpenseCategory;
      vendor?: string;
      paymentMethod?: 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check';
      frequency?: 'monthly' | 'quarterly' | 'yearly';
      dueDate?: string;
      startDate?: string;
      endDate?: string;
      isAmortized?: boolean;
      amortizationMonths?: number;
      expenseDate?: string;
      receiptUrl?: string;
      notes?: string;
      caseId?: string;
    }): Promise<Expense> => {
      const orgId = await getCurrentOrganizationId();
      if (!orgId) throw new Error('No organization found for user');

      const { data: { user } } = await supabase.auth.getUser();

      const insertData: TablesInsert<'expenses'> = {
        organization_id: orgId,
        type: expenseData.type,
        name: expenseData.name,
        amount: expenseData.amount,
        category: expenseData.category,
        vendor: expenseData.vendor || null,
        payment_method: expenseData.paymentMethod || null,
        frequency: expenseData.frequency || null,
        due_date: expenseData.dueDate || null,
        start_date: expenseData.startDate || null,
        end_date: expenseData.endDate || null,
        is_amortized: expenseData.isAmortized || false,
        amortization_months: expenseData.amortizationMonths || null,
        expense_date: expenseData.expenseDate || null,
        receipt_url: expenseData.receiptUrl || null,
        notes: expenseData.notes || null,
        case_id: expenseData.caseId || null,
        created_by: user?.id,
      };

      const { data, error } = await supabase
        .from('expenses')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return mapDbExpenseToExpense(data);
    },

    update: async (id: string, expenseData: Partial<{
      type: ExpenseType;
      name: string;
      amount: number;
      category: ExpenseCategory;
      vendor?: string;
      paymentMethod?: 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check';
      frequency?: 'monthly' | 'quarterly' | 'yearly';
      dueDate?: string;
      startDate?: string;
      endDate?: string;
      isAmortized?: boolean;
      amortizationMonths?: number;
      expenseDate?: string;
      receiptUrl?: string;
      notes?: string;
    }>): Promise<Expense> => {
      const { data: { user } } = await supabase.auth.getUser();

      const updateData: TablesUpdate<'expenses'> = {
        updated_by: user?.id,
        updated_at: new Date().toISOString(),
      };

      if (expenseData.type !== undefined) updateData.type = expenseData.type;
      if (expenseData.name !== undefined) updateData.name = expenseData.name;
      if (expenseData.amount !== undefined) updateData.amount = expenseData.amount;
      if (expenseData.category !== undefined) updateData.category = expenseData.category;
      if (expenseData.vendor !== undefined) updateData.vendor = expenseData.vendor;
      if (expenseData.paymentMethod !== undefined) updateData.payment_method = expenseData.paymentMethod;
      if (expenseData.frequency !== undefined) updateData.frequency = expenseData.frequency;
      if (expenseData.dueDate !== undefined) updateData.due_date = expenseData.dueDate;
      if (expenseData.startDate !== undefined) updateData.start_date = expenseData.startDate;
      if (expenseData.endDate !== undefined) updateData.end_date = expenseData.endDate;
      if (expenseData.isAmortized !== undefined) updateData.is_amortized = expenseData.isAmortized;
      if (expenseData.amortizationMonths !== undefined) updateData.amortization_months = expenseData.amortizationMonths;
      if (expenseData.expenseDate !== undefined) updateData.expense_date = expenseData.expenseDate;
      if (expenseData.receiptUrl !== undefined) updateData.receipt_url = expenseData.receiptUrl;
      if (expenseData.notes !== undefined) updateData.notes = expenseData.notes;

      const { data, error } = await supabase
        .from('expenses')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return mapDbExpenseToExpense(data);
    },

    delete: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },

    getMonthlySummary: async (month: number, year: number): Promise<MonthlyExpenseSummary> => {
      const startDate = new Date(year, month - 1, 1).toISOString();
      const endDate = new Date(year, month, 0).toISOString();

      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .or(`start_date.lte.${endDate},expense_date.gte.${startDate}`)
        .or(`end_date.is.null,end_date.gte.${startDate}`);

      if (error) throw error;

      const expenses = (data || []).map(mapDbExpenseToExpense);
      
      let totalRecurring = 0;
      let totalSingle = 0;
      const byCategory: Partial<Record<ExpenseCategory, number>> = {};
      const upcomingPayments: MonthlyExpenseSummary['upcomingPayments'] = [];

      expenses.forEach((expense) => {
        const amount = expense.isAmortized && expense.amortizationMonths
          ? expense.amount / expense.amortizationMonths
          : expense.amount;

        if (expense.type === 'recurring') {
          totalRecurring += amount;
        } else {
          totalSingle += amount;
        }

        const category = expense.category;
        byCategory[category] = (byCategory[category] || 0) + amount;

        if (expense.dueDate) {
          upcomingPayments.push({
            id: expense.id,
            name: expense.name,
            amount: expense.amount,
            dueDate: expense.dueDate,
          });
        }
      });

      return {
        month,
        year,
        totalRecurring,
        totalSingle,
        total: totalRecurring + totalSingle,
        byCategory,
        upcomingPayments: upcomingPayments.sort(
          (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        ),
      };
    },
  },

  // Legacy cost endpoints (mapped to expenses)
  fixedCosts: {
    getAll: async () => {
      const expenses = await apiService.expenses.getByType('recurring');
      return expenses.map((e) => ({
        id: e.id,
        name: e.name,
        amount: e.amount,
        frequency: e.frequency || 'monthly',
        category: e.category,
        paymentMethod: e.paymentMethod,
        dueDate: e.dueDate,
        vendorInfo: e.vendor,
        notes: e.notes,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
      }));
    },
    create: async (data: { name: string; amount: number; frequency: 'monthly' | 'quarterly' | 'yearly'; category?: string; paymentMethod?: string; dueDate?: string; vendorInfo?: string; notes?: string }) => {
      const expense = await apiService.expenses.create({
        type: 'recurring',
        name: data.name,
        amount: data.amount,
        frequency: data.frequency,
        category: (data.category as ExpenseCategory) || 'other',
        paymentMethod: data.paymentMethod as 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check',
        dueDate: data.dueDate,
        vendor: data.vendorInfo,
        notes: data.notes,
        startDate: new Date().toISOString(),
      });
      return {
        id: expense.id,
        name: expense.name,
        amount: expense.amount,
        frequency: expense.frequency || 'monthly',
        category: expense.category,
        paymentMethod: expense.paymentMethod,
        dueDate: expense.dueDate,
        vendorInfo: expense.vendor,
        notes: expense.notes,
        createdAt: expense.createdAt,
        updatedAt: expense.updatedAt,
      };
    },
    update: async (id: string, data: Partial<{ name: string; amount: number; frequency: 'monthly' | 'quarterly' | 'yearly'; category?: string; paymentMethod?: string; dueDate?: string; vendorInfo?: string; notes?: string }>) => {
      const expense = await apiService.expenses.update(id, {
        name: data.name,
        amount: data.amount,
        frequency: data.frequency,
        category: data.category as ExpenseCategory,
        paymentMethod: data.paymentMethod as 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check',
        dueDate: data.dueDate,
        vendor: data.vendorInfo,
        notes: data.notes,
      });
      return {
        id: expense.id,
        name: expense.name,
        amount: expense.amount,
        frequency: expense.frequency || 'monthly',
        category: expense.category,
        paymentMethod: expense.paymentMethod,
        dueDate: expense.dueDate,
        vendorInfo: expense.vendor,
        notes: expense.notes,
        createdAt: expense.createdAt,
        updatedAt: expense.updatedAt,
      };
    },
    delete: (id: string) => apiService.expenses.delete(id),
  },

  variableCosts: {
    getAll: async () => {
      const expenses = await apiService.expenses.getByType('single');
      return expenses.map((e) => ({
        id: e.id,
        description: e.name,
        amount: e.amount,
        date: e.expenseDate || e.createdAt,
        category: e.category,
        paymentMethod: e.paymentMethod,
        vendorInfo: e.vendor,
        notes: e.notes,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
      }));
    },
    create: async (data: { description: string; amount: number; date: string; category?: string; paymentMethod?: string; vendorInfo?: string; caseId?: string; notes?: string }) => {
      const expense = await apiService.expenses.create({
        type: 'single',
        name: data.description,
        amount: data.amount,
        expenseDate: data.date,
        category: (data.category as ExpenseCategory) || 'other',
        paymentMethod: data.paymentMethod as 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check',
        vendor: data.vendorInfo,
        caseId: data.caseId,
        notes: data.notes,
      });
      return {
        id: expense.id,
        description: expense.name,
        amount: expense.amount,
        date: expense.expenseDate || expense.createdAt,
        category: expense.category,
        paymentMethod: expense.paymentMethod,
        vendorInfo: expense.vendor,
        notes: expense.notes,
        createdAt: expense.createdAt,
        updatedAt: expense.updatedAt,
      };
    },
    update: async (id: string, data: Partial<{ description: string; amount: number; date: string; category?: string; paymentMethod?: string; vendorInfo?: string; notes?: string }>) => {
      const expense = await apiService.expenses.update(id, {
        name: data.description,
        amount: data.amount,
        expenseDate: data.date,
        category: data.category as ExpenseCategory,
        paymentMethod: data.paymentMethod as 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'check',
        vendor: data.vendorInfo,
        notes: data.notes,
      });
      return {
        id: expense.id,
        description: expense.name,
        amount: expense.amount,
        date: expense.expenseDate || expense.createdAt,
        category: expense.category,
        paymentMethod: expense.paymentMethod,
        vendorInfo: expense.vendor,
        notes: expense.notes,
        createdAt: expense.createdAt,
        updatedAt: expense.updatedAt,
      };
    },
    delete: (id: string) => apiService.expenses.delete(id),
  },

  // Auth endpoints (delegated to supabaseClient)
  auth: {
    login: async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      const profile = await getCurrentOrganizationId();
      
      return {
        token: data.session?.access_token || '',
        refreshToken: data.session?.refresh_token || '',
        user: {
          id: data.user?.id || '',
          email: data.user?.email || '',
          name: data.user?.email || '',
          role: 'regular' as const,
          organizationId: profile,
          isActive: true,
          createdAt: data.user?.created_at || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        expiresIn: 3600,
      };
    },
    logout: () => supabase.auth.signOut(),
    getCurrentUser: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      return {
        id: user.id,
        email: user.email || '',
        name: profile ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') : user.email || '',
        role: (profile?.role === 'superadmin' ? 'superadmin' : profile?.role === 'admin' ? 'admin' : 'regular') as 'regular' | 'admin' | 'superadmin',
        organizationId: profile?.organization_id || null,
        isActive: profile?.is_active ?? true,
        createdAt: profile?.created_at || user.created_at || new Date().toISOString(),
        updatedAt: profile?.updated_at || new Date().toISOString(),
      };
    },
    refreshToken: async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      return {
        token: data.session?.access_token || '',
        refreshToken: data.session?.refresh_token || '',
        user: {
          id: data.user?.id || '',
          email: data.user?.email || '',
          name: data.user?.email || '',
          role: 'regular' as const,
          organizationId: null,
          isActive: true,
          createdAt: data.user?.created_at || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        expiresIn: 3600,
      };
    },
  },

  // Security & Access Control (ISO 27001 A.9)
  security: {
    // MFA
    getMFAStatus: async (): Promise<MFAConfig> => {
      return callAuthService<MFAConfig>('GET', 'mfa/status');
    },

    setupMFA: async (): Promise<MFASetupResponse> => {
      return callAuthService<MFASetupResponse>('POST', 'mfa/setup');
    },

    verifyMFA: async (code: string, isSetup?: boolean): Promise<{ verified: boolean; backupCodes?: string[] }> => {
      return callAuthService<{ verified: boolean; backupCodes?: string[] }>('POST', 'mfa/verify', { code, isSetup });
    },

    disableMFA: async (code: string): Promise<void> => {
      await callAuthService<void>('POST', 'mfa/disable', { code });
    },

    getBackupCodes: async (): Promise<{ codes: string[]; usedCount: number }> => {
      return callAuthService<{ codes: string[]; usedCount: number }>('GET', 'mfa/backup-codes');
    },

    regenerateBackupCodes: async (code: string): Promise<{ codes: string[] }> => {
      return callAuthService<{ codes: string[] }>('POST', 'mfa/regenerate-backup-codes', { code });
    },

    // Sessions
    getActiveSessions: async (): Promise<UserSession[]> => {
      return callAuthService<UserSession[]>('GET', 'sessions');
    },

    revokeSession: async (sessionId: string): Promise<void> => {
      await callAuthService<void>('DELETE', `sessions/${sessionId}`);
    },

    revokeAllOtherSessions: async (): Promise<{ revokedCount: number }> => {
      return callAuthService<{ revokedCount: number }>('POST', 'sessions/revoke-others');
    },

    // Password
    validatePassword: async (password: string): Promise<PasswordValidationResult> => {
      return callAuthService<PasswordValidationResult>('POST', 'password/validate', { password });
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
      await callAuthService<void>('POST', 'password/change', { currentPassword, newPassword });
    },

    // Login history
    getLoginHistory: async (limit?: number): Promise<LoginAttempt[]> => {
      const path = limit ? `login-history?limit=${limit}` : 'login-history';
      return callAuthService<LoginAttempt[]>('GET', path);
    },

    // Security settings (admin only)
    getSecuritySettings: async (): Promise<SecuritySettings> => {
      return callAuthService<SecuritySettings>('GET', 'settings');
    },

    updateSecuritySettings: async (settings: Partial<SecuritySettings>): Promise<SecuritySettings> => {
      return callAuthService<SecuritySettings>('PUT', 'settings', settings as Record<string, unknown>);
    },
  },

  // Audit Logs (ISO 27001 A.12.4 - admin only)
  auditLogs: {
    getLogs: async (filters: AuditLogFilters = {}): Promise<{ logs: AuditLog[]; total: number; limit: number; offset: number }> => {
      const params = new URLSearchParams();
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.action) params.set('action', filters.action);
      if (filters.resourceType) params.set('resourceType', filters.resourceType);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.piiAccessed !== undefined) params.set('piiAccessed', String(filters.piiAccessed));
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));
      if (filters.organizationId) params.set('organizationId', filters.organizationId);

      const queryString = params.toString();
      const path = queryString ? `logs?${queryString}` : 'logs';
      return callAuditService<{ logs: AuditLog[]; total: number; limit: number; offset: number }>('GET', path);
    },

    exportLogs: async (filters: AuditLogFilters = {}, format: 'csv' | 'json' = 'csv'): Promise<Blob> => {
      const params: Record<string, string> = { format };
      if (filters.userId) params.userId = filters.userId;
      if (filters.action) params.action = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.piiAccessed !== undefined) params.piiAccessed = String(filters.piiAccessed);
      if (filters.severity) params.severity = filters.severity;
      if (filters.organizationId) params.organizationId = filters.organizationId;

      return callEdgeFunctionBlob(AUDIT_SERVICE_URL, 'export', params);
    },

    getActions: async (): Promise<string[]> => {
      const result = await callAuditService<{ actions: string[] }>('GET', 'actions');
      return result.actions;
    },

    getResourceTypes: async (): Promise<string[]> => {
      const result = await callAuditService<{ resourceTypes: string[] }>('GET', 'resource-types');
      return result.resourceTypes;
    },

    createLog: async (logData: {
      action: string;
      resourceType: string;
      resourceId?: string;
      dataBefore?: Record<string, unknown>;
      dataAfter?: Record<string, unknown>;
      piiAccessed?: boolean;
      consentRelated?: boolean;
      severity?: 'info' | 'warning' | 'critical';
    }): Promise<{ id: string }> => {
      return callAuditService<{ id: string }>('POST', 'log', logData as Record<string, unknown>);
    },
  },

  // Security Monitoring (ISO 27001 A.16)
  securityMonitor: {
    getDashboard: async (range: '24h' | '7d' | '30d' = '24h'): Promise<SecurityDashboardData> => {
      return callSecurityMonitor<SecurityDashboardData>('GET', `dashboard?range=${range}`);
    },

    getEvents: async (filters: SecurityEventFilters = {}): Promise<{ events: SecurityEvent[]; total: number; limit: number; offset: number }> => {
      const params = new URLSearchParams();
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.eventType) params.set('eventType', filters.eventType);
      if (filters.resolved !== undefined) params.set('resolved', String(filters.resolved));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));

      const queryString = params.toString();
      const path = queryString ? `events?${queryString}` : 'events';
      return callSecurityMonitor<{ events: SecurityEvent[]; total: number; limit: number; offset: number }>('GET', path);
    },

    resolveEvent: async (eventId: string, notes?: string): Promise<{ resolved: boolean }> => {
      return callSecurityMonitor<{ resolved: boolean }>('PUT', `events/${eventId}/resolve`, { notes });
    },

    runScan: async (): Promise<{ scanned: boolean; detected: number; inserted: number; events: SecurityEvent[] }> => {
      return callSecurityMonitor<{ scanned: boolean; detected: number; inserted: number; events: SecurityEvent[] }>('POST', 'scan');
    },

    getRateLimits: async (): Promise<{ rateLimits: RateLimitInfo[] }> => {
      return callSecurityMonitor<{ rateLimits: RateLimitInfo[] }>('GET', 'rate-limits');
    },

    checkRateLimit: async (key: string, type: string): Promise<RateLimitCheckResult> => {
      return callSecurityMonitor<RateLimitCheckResult>('POST', 'rate-limit/check', { key, type });
    },
  },

  // Encryption & Key Management (ISO 27001 A.10)
  keyManagement: {
    getStatus: async (): Promise<KeyRotationStatus> => {
      return callKeyManagement<KeyRotationStatus>('GET', 'status');
    },

    getKeys: async (): Promise<{ keys: EncryptionKey[] }> => {
      return callKeyManagement<{ keys: EncryptionKey[] }>('GET', 'keys');
    },

    rotateKey: async (): Promise<KeyRotationResult> => {
      return callKeyManagement<KeyRotationResult>('POST', 'rotate');
    },

    reencryptData: async (batchSize?: number): Promise<{ processed: number; remaining: number; status: string }> => {
      return callKeyManagement<{ processed: number; remaining: number; status: string }>('POST', 'reencrypt', { batchSize });
    },

    verifyEncryption: async (): Promise<{ verified: boolean; totalRecords: number; encryptedRecords: number; issues: string[] }> => {
      return callKeyManagement<{ verified: boolean; totalRecords: number; encryptedRecords: number; issues: string[] }>('GET', 'verify');
    },
  },

  // User Data / GDPR Rights (consent, export, deletion)
  userData: {
    getConsent: async (): Promise<ConsentStatus> => {
      return callUserDataService<ConsentStatus>('GET', 'consent');
    },

    updateConsent: async (data: Partial<ConsentUpdate>): Promise<{ message: string; consent: ConsentStatus }> => {
      return callUserDataService<{ message: string; consent: ConsentStatus }>('PUT', 'consent', data as Record<string, unknown>);
    },

    getConsentHistory: async (): Promise<ConsentHistoryEntry[]> => {
      return callUserDataService<ConsentHistoryEntry[]>('GET', 'history');
    },

    exportData: async (): Promise<UserDataExport> => {
      return callUserDataService<UserDataExport>('GET', 'export');
    },

    requestDeletion: async (reason?: string): Promise<DeletionRequest> => {
      return callUserDataService<DeletionRequest>('POST', 'delete', reason ? { reason } : undefined);
    },

    cancelDeletion: async (): Promise<{ message: string }> => {
      return callUserDataService<{ message: string }>('DELETE', 'delete');
    },

    getDeletionRequests: async (): Promise<DeletionRequest[]> => {
      return callUserDataService<DeletionRequest[]>('GET', 'delete');
    },
  },

  // Data Lifecycle / Retention (ISO 27001 A.8.10 - admin only)
  dataLifecycle: {
    getPolicies: async (): Promise<{ policies: import('../types').RetentionPolicy[] }> => {
      return callDataLifecycleService<{ policies: import('../types').RetentionPolicy[] }>('GET', 'policies');
    },

    updatePolicy: async (
      id: string,
      data: {
        retentionPeriodDays?: number;
        actionAfterExpiry?: 'delete' | 'anonymize' | 'archive';
        enabled?: boolean;
      }
    ): Promise<{ policy: import('../types').RetentionPolicy; message: string }> => {
      return callDataLifecycleService<{ policy: import('../types').RetentionPolicy; message: string }>(
        'PUT',
        `policies/${id}`,
        data as Record<string, unknown>
      );
    },

    triggerAnonymization: async (
      recordType: string
    ): Promise<{ anonymizedCount: number; recordType: string; anonymizedFields: string[]; message: string }> => {
      return callDataLifecycleService<{
        anonymizedCount: number;
        recordType: string;
        anonymizedFields: string[];
        message: string;
      }>('POST', 'anonymize', { recordType });
    },

    getUpcomingDeletions: async (): Promise<{
      items: Array<{ dataType: string; action: string; recordCount: number; expiryDate: string }>;
    }> => {
      return callDataLifecycleService<{
        items: Array<{ dataType: string; action: string; recordCount: number; expiryDate: string }>;
      }>('GET', 'upcoming-deletions');
    },

    getAnonymizationLog: async (
      params: { limit?: number; offset?: number; recordType?: string } = {}
    ): Promise<{
      entries: import('../types').AnonymizationLog[];
      total: number;
      limit: number;
      offset: number;
    }> => {
      const searchParams = new URLSearchParams();
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.offset) searchParams.set('offset', String(params.offset));
      if (params.recordType) searchParams.set('recordType', params.recordType);

      const queryString = searchParams.toString();
      const path = queryString ? `anonymization-log?${queryString}` : 'anonymization-log';
      return callDataLifecycleService<{
        entries: import('../types').AnonymizationLog[];
        total: number;
        limit: number;
        offset: number;
      }>('GET', path);
    },

    executeRetention: async (): Promise<{
      results: Array<{ dataType: string; action: string; processedCount: number }>;
      executedAt: string;
      message: string;
    }> => {
      return callDataLifecycleService<{
        results: Array<{ dataType: string; action: string; processedCount: number }>;
        executedAt: string;
        message: string;
      }>('POST', 'execute-retention');
    },
  },

  // Account lockout RPCs (called before/after authentication)
  // Note: These RPC functions exist in the database but aren't in auto-generated types
  accountLockout: {
    // Check if account is locked (can be called without auth)
    checkLocked: async (email: string): Promise<{
      is_locked: boolean;
      locked_until: string | null;
      failed_attempts: number;
      max_attempts: number;
      remaining_attempts: number;
    }> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('check_account_locked', {
        p_email: email,
      });

      if (error) {
        console.error('Error checking account lockout:', error);
        // Return a safe default on error
        return {
          is_locked: false,
          locked_until: null,
          failed_attempts: 0,
          max_attempts: 5,
          remaining_attempts: 5,
        };
      }

      return data as {
        is_locked: boolean;
        locked_until: string | null;
        failed_attempts: number;
        max_attempts: number;
        remaining_attempts: number;
      };
    },

    // Record a login attempt (can be called without auth)
    recordAttempt: async (params: {
      email: string;
      userId?: string;
      ipAddress?: string;
      userAgent?: string;
      success: boolean;
      failureReason?: string;
    }): Promise<{
      recorded: boolean;
      is_locked: boolean;
      locked_until: string | null;
      failed_attempts: number;
      remaining_attempts: number;
    }> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('record_login_attempt', {
        p_email: params.email,
        p_user_id: params.userId || null,
        p_ip_address: params.ipAddress || null,
        p_user_agent: params.userAgent || null,
        p_success: params.success,
        p_failure_reason: params.failureReason || null,
      });

      if (error) {
        console.error('Error recording login attempt:', error);
        // Return a safe default on error
        return {
          recorded: false,
          is_locked: false,
          locked_until: null,
          failed_attempts: 0,
          remaining_attempts: 5,
        };
      }

      return data as {
        recorded: boolean;
        is_locked: boolean;
        locked_until: string | null;
        failed_attempts: number;
        remaining_attempts: number;
      };
    },
  },

  // Email service endpoints
  email: {
    sendInvitation: async (invitationToken: string): Promise<{ success: boolean; message: string }> => {
      return callEmailService('POST', 'send-invitation', { invitationToken });
    },
    sendTesterWelcome: async (data: {
      email: string;
      firstName: string;
      role: string;
      organizationName?: string;
      recoveryLink?: string;
    }): Promise<{ success: boolean; message: string }> => {
      return callEmailService('POST', 'send-tester-welcome', data);
    },
  },

  // Tester feedback endpoints
  testerFeedback: {
    submit: async (feedback: {
      type: 'question' | 'complaint' | 'feature' | 'other';
      message: string;
    }): Promise<{ success: true; feedbackId: string; message: string }> => {
      return callBitacoraService('POST', 'feedback', {
        feedbackType: feedback.type,
        message: feedback.message,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });
    },
  },

  // Plazos (Deadlines) endpoints
  plazos: {
    getAll: async (filters?: { caseId?: string; from?: string; to?: string; estado?: string }) => {
      const queryParams: Record<string, string> = {};
      if (filters?.caseId) queryParams.case_id = filters.caseId;
      if (filters?.from) queryParams.from = filters.from;
      if (filters?.to) queryParams.to = filters.to;
      if (filters?.estado) queryParams.estado = filters.estado;
      return callPlazoService<any[]>('GET', '', undefined, queryParams);
    },
    getById: async (id: string) => {
      return callPlazoService<any>('GET', id);
    },
    create: async (data: { caseId: string; titulo: string; descripcion?: string; fechaVencimiento: string; diasAnticipacion: number }) => {
      return callPlazoService<any>('POST', '', data as unknown as Record<string, unknown>);
    },
    update: async (id: string, data: Record<string, unknown>) => {
      return callPlazoService<any>('PUT', id, data);
    },
    delete: async (id: string) => {
      return callPlazoService<void>('DELETE', id);
    },
    complete: async (id: string) => {
      return callPlazoService<any>('POST', `${id}/complete`);
    },
    googleCalendar: {
      getAuthUrl: async () => {
        return callPlazoService<{ url: string }>('GET', 'google-calendar/auth-url', undefined, { origin: window.location.origin });
      },
      handleCallback: async (code: string, userId: string) => {
        return callPlazoService<{ success: boolean }>('POST', 'google-calendar/callback', { code, userId });
      },
      getStatus: async () => {
        return callPlazoService<{ connected: boolean }>('GET', 'google-calendar/status');
      },
      disconnect: async () => {
        return callPlazoService<{ success: boolean }>('POST', 'google-calendar/disconnect');
      },
    },
  },

  // Notifications endpoints
  notifications: {
    getAll: async (unreadOnly?: boolean) => {
      const queryParams: Record<string, string> = {};
      if (unreadOnly) queryParams.unread_only = 'true';
      return callPlazoService<any[]>('GET', 'notifications', undefined, queryParams);
    },
    markAsRead: async (id: string) => {
      return callPlazoService<{ success: boolean }>('PUT', `notifications/${id}/read`);
    },
    markAllAsRead: async () => {
      return callPlazoService<{ success: boolean }>('PUT', 'notifications/read-all');
    },
  },

  // Feature Waitlist endpoints
  featureWaitlist: {
    subscribe: async (featureName: string, email: string) => {
      return callFeatureWaitlistService<{ message: string; success?: boolean; alreadySubscribed?: boolean }>(
        'POST',
        '',
        { feature_name: featureName, email }
      );
    },
    getAll: async (featureName?: string) => {
      const queryParams: Record<string, string> = {};
      if (featureName) {
        queryParams.feature = featureName;
      }
      return callFeatureWaitlistService<FeatureWaitlistEntry[]>(
        'GET',
        '',
        undefined,
        queryParams
      );
    },
  },
};

export default supabase;
